import type React from "react";
import { useTranslation } from "react-i18next";
import { FaCircle, FaStop } from "react-icons/fa6";
import { Loading } from "../Loading";

interface BroadcastButtonProps {
  height: number;
  compact?: boolean;
  onLive: boolean;
  isLoading?: boolean;
  disabled: boolean;
  showAssembler: boolean;
  haveOutputStream: boolean;
  onStart: () => void;
  onStop: () => void;
}

const NO_DRAG_REGION_CLASS = "[-webkit-app-region:no-drag]";

export const BroadcastButton: React.FC<BroadcastButtonProps> = ({
  height,
  compact = false,
  onLive,
  isLoading = false,
  disabled,
  showAssembler,
  haveOutputStream,
  onStart,
  onStop,
}) => {
  const { t } = useTranslation("dashboard");

  const label = isLoading
    ? t("DASHBOARD.starting")
    : onLive
      ? t("DASHBOARD.powerOff")
      : t("DASHBOARD.MENUBAR.broadcast");
  const tooltip = isLoading
    ? t("DASHBOARD.starting")
    : onLive
      ? t("DASHBOARD.powerOff")
      : showAssembler
        ? t("DASHBOARD.broadcastRequiresPreview")
        : disabled && !haveOutputStream
          ? t("DASHBOARD.streamingTooltip")
          : t("DASHBOARD.MENUBAR.broadcast");

  const buttonHeight = Math.max(24, Math.min(32, height - 6));

  const className = [
    NO_DRAG_REGION_CLASS,
    "inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 sm:px-4",
    "text-[11px] font-semibold tracking-wide ",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
    "disabled:cursor-not-allowed disabled:opacity-45",
    "active:scale-[0.97]",
    compact ? "w-8 px-0 sm:w-9" : "",
    onLive
      ? "border-red-500/40 bg-red-500/15 text-red-400 hover:bg-red-500/25 hover:border-red-500/60 shadow-[0_0_16px_-4px_rgba(239,68,68,0.3)]"
      : "border-accent/30 bg-accent/10 text-accent hover:bg-accent/20 hover:border-accent/50",
  ].join(" ");

  return (
    <button
      type="button"
      className={className}
      style={{ height: `${buttonHeight}px` }}
      aria-label={label}
      title={tooltip}
      disabled={(disabled && !onLive) || isLoading}
      onClick={onLive ? onStop : onStart}
    >
      {isLoading ? (
        <Loading size={12} className="text-current" />
      ) : onLive ? (
        <FaStop size={8} />
      ) : (
        <FaCircle size={7} />
      )}
      {!compact && <span>{label}</span>}
    </button>
  );
};
